import React from 'react';

import { formatHours } from './formatHours';

// GRAVY-10073: per-row "Hours This Week" indicator on the staffing worker
// list. Renders `N/40 hrs`. The whole string (worker-hours value AND the
// `/40 hrs` threshold suffix) flips to red together when the worker has
// already exceeded 40 OR when the admin's current-week selection would
// tip them over. The two parts share one color so the cell reads as a
// single unit per the approved UI design.
//
// `selectedShiftHours` is filtered by the wizard parent to **only the
// shifts in the worker's current Monday-anchored UTC week** — so a
// next-week-only selection on a 40h-this-week worker keeps the cell
// neutral (no admin action against the impacted week).
//
// `hoursThisWeek` is `null` for 1099 workers (the OT projection is W2-only
// on the backend) — we render an em-dash so the column layout stays
// consistent without implying a meaningful zero.

const FONT = "'Manrope', sans-serif";
const RED = '#D14848';
const NEUTRAL = '#1C1C1C';
const OT_THRESHOLD = 40;

type Props = {
  hoursThisWeek: number | null | undefined;
  /**
   * Hours of the currently-selected shifts that fall within the worker's
   * **current Monday-anchored UTC week** (NOT the cumulative total of
   * every selected shift). The wizard parent filters by week before
   * passing this through.
   */
  selectedShiftHours?: number;
};

export const HoursThisWeekCell: React.FC<Props> = ({
  hoursThisWeek,
  selectedShiftHours = 0,
}) => {
  if (hoursThisWeek == null) {
    return (
      <span
        data-testid="hours-this-week-cell"
        style={{ fontFamily: FONT, color: NEUTRAL }}
      >
        &mdash;
      </span>
    );
  }
  // Red when the worker has already exceeded 40h this week OR when the
  // admin's CURRENT-WEEK selection would tip them over. The
  // `hoursThisWeek > OT_THRESHOLD` branch covers already-exceeded workers
  // even with no selection; the projected branch covers the about-to-
  // exceed action. A worker exactly at 40 with no current-week selection
  // stays neutral (40 is not "exceeding").
  //
  // Color is set on BOTH the outer span and the inner <strong> tag.
  // Browser defaults and MUI's CSS resets can override `color` inheritance
  // on <strong> in some surfaces, so applying the inline color only on
  // the outer span leaves the worker-hours number rendered in the
  // default text color while the "/40 hrs" suffix correctly turns red.
  // Explicit color on both sides keeps the full string visually unified.
  const projected = hoursThisWeek + selectedShiftHours;
  const atRisk = projected > OT_THRESHOLD;
  const color = atRisk ? RED : NEUTRAL;
  return (
    <span
      data-testid="hours-this-week-cell"
      data-at-risk={atRisk ? 'true' : 'false'}
      style={{
        fontFamily: FONT,
        fontWeight: 600,
        color,
      }}
    >
      <strong data-testid="hours-this-week-value" style={{ color }}>
        {formatHours(hoursThisWeek)}
      </strong>
      /{OT_THRESHOLD} hrs
    </span>
  );
};

export default HoursThisWeekCell;
